import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";

const STEPS = [
  { key: "age", q: "Which stage of life are you in?", opts: ["Under 30", "30–45", "45–60", "60+"] },
  { key: "relationship", q: "What's your relationship situation?", opts: ["Single", "In a relationship", "Married / partnered", "Prefer not to say"] },
  { key: "children", q: "Do you have children who depend on you?", opts: ["No children", "Expecting", "Yes, young children", "Yes, older children"] },
  { key: "employment", q: "How would you describe your work?", opts: ["Employed (PAYE)", "Self-employed", "Business owner", "Not currently working"] },
  { key: "home", q: "What's your home situation?", opts: ["Renting", "Buying my first home", "Own with a mortgage", "Own, mortgage-free"] },
  { key: "insurance", q: "Do you currently have any insurance cover?", opts: ["None", "Only through work", "Some personal cover", "Not sure"] },
  { key: "goal", q: "What matters most to you right now?", opts: ["Protecting my family", "Buying / managing a home", "Protecting my income", "Just having a look"] },
];

function buildRecommendations(a) {
  const recs = new Set();
  if (["Yes, young children", "Yes, older children", "Expecting"].includes(a.children) || a.relationship === "Married / partnered") recs.add("Life Insurance");
  if (a.home === "Own with a mortgage" || a.home === "Buying my first home") { recs.add("Mortgage Protection Insurance"); }
  if (["Self-employed", "Business owner", "Employed (PAYE)"].includes(a.employment)) recs.add("Income Protection");
  if (a.goal === "Protecting my income") recs.add("Income Protection");
  if (a.goal === "Protecting my family") { recs.add("Life Insurance"); recs.add("Trauma Insurance"); }
  recs.add("Health Insurance");
  return [...recs].slice(0, 4);
}

export default function Journey() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const total = STEPS.length;
  const current = STEPS[step];

  const choose = (opt) => {
    const next = { ...answers, [current.key]: opt };
    setAnswers(next);
    if (step < total - 1) setStep(step + 1);
    else setDone(true);
  };

  const recs = done ? buildRecommendations(answers) : [];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/journey", {
        name: lead.name, email: lead.email, phone: lead.phone,
        message: "Completed guided journey",
        source: "journey",
        meta: { answers, recommendations: recs },
      });
      setSubmitted(true);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <Seo
        title="Find the Right Insurance Cover | Quantum Cover"
        description="Answer a few quick questions and see which insurance is worth talking through with an adviser. It's free, and there's no pressure."
        path="/journey"
      />
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        {!done ? (
          <>
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="font-medium text-slate-500">Question {step + 1} of {total}</span>
              <span className="text-emerald-700 font-medium">{Math.round(((step) / total) * 100)}%</span>
            </div>
            <Progress value={(step / total) * 100} className="h-2 mb-10" data-testid="journey-progress" />

            <div key={step} className="qc-fade-up">
              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-medium mb-4"><Sparkles size={15} /> A few quick questions</span>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0B3D2E] tracking-tight">{current.q}</h1>
              <div className="mt-8 grid gap-3">
                {current.opts.map((opt) => (
                  <button
                    key={opt}
                    data-testid={`journey-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => choose(opt)}
                    className="group flex items-center justify-between text-left bg-white rounded-2xl border-2 border-slate-200 px-6 py-5 hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                  >
                    <span className="font-medium text-[#0B3D2E]">{opt}</span>
                    <ArrowRight size={18} className="text-slate-300 group-hover:text-emerald-600 transition-colors" />
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="journey-back-btn">
                  <ArrowLeft size={15} /> Back
                </button>
              )}
            </div>
          </>
        ) : submitted ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] qc-fade-up" data-testid="journey-thankyou">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto"><Check size={32} /></span>
            <h1 className="mt-6 font-display text-3xl font-semibold text-[#0B3D2E]">You're sorted, {lead.name.split(" ")[0]}!</h1>
            <p className="mt-3 text-slate-600">A licensed adviser will be in touch within one business day. While you wait, have a read about the cover we've suggested.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/booking"><Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-6">Book a time now</Button></Link>
              <Link to="/knowledge"><Button variant="outline" className="rounded-full px-6 py-6">Have a read first</Button></Link>
            </div>
          </div>
        ) : (
          <div className="qc-fade-up">
            <div className="bg-[#0B3D2E] rounded-3xl p-8 text-white mb-6">
              <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-medium mb-3"><Sparkles size={15} /> Your summary</span>
              <h1 className="font-display text-3xl font-semibold">Here's what's worth a look</h1>
              <p className="mt-3 text-slate-300">Here's what looks worth a closer look, based on what you told us. It's general info, not personal advice. A licensed adviser can talk it through properly for your situation.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {recs.map((r) => (
                  <div key={r} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                    <ShieldCheck size={20} className="text-emerald-400 shrink-0" /><span className="font-medium">{r}</span>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <h2 className="font-display text-xl font-semibold text-[#0B3D2E]">Get your summary and a free chat</h2>
              <p className="text-slate-600 mt-1 text-sm">Pop your details in and a licensed adviser will give you a call. No obligation to go ahead with anything.</p>
              <div className="mt-6 space-y-3">
                <Input required placeholder="Full name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="journey-name" className="rounded-xl h-12" />
                <Input required type="email" placeholder="Email address" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="journey-email" className="rounded-xl h-12" />
                <Input placeholder="Phone (optional)" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="journey-phone" className="rounded-xl h-12" />
              </div>
              <Button type="submit" disabled={loading} data-testid="journey-submit" className="mt-6 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base">
                {loading ? "Sending…" : "Send my summary"}
              </Button>
              <p className="mt-4 text-xs text-slate-400 text-center">Your details stay private. We only use them to set up your chat.</p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
